import * as React from 'react';

import { cn } from '../../styles/lib/utils';
import { Button, IconButton } from '../Button';
import type { IconName } from '../Icon';
import type { ServerSideSelectionState } from '../ServerTable';

export type BulkActionOverlayAction = {
  label: string;
  /** Icon name from the Icon component, or a custom React node (e.g. SvgIcon). */
  icon: IconName | React.ReactNode;
  onClick: (selection: ServerSideSelectionState) => void;
  /** data-testid for the action's button. */
  testId?: string;
};

export type BulkActionOverlayProps = {
  /** Actions to show in the overlay (each has label, icon, onClick). */
  actions: BulkActionOverlayAction[];
  /** Current selection; passed to each action's onClick. */
  selection: ServerSideSelectionState;
  /** Called when the user clicks the close (X) button. */
  onClose: () => void;
  className?: string;
};

/**
 * Floating pill overlay for bulk actions when rows are selected.
 * Rendered by ServerTable; parent positions it (z-index 40, centered, top 50% of the visible height).
 * Width is capped to the parent minus room for the checkbox column on each side; actions wrap
 * onto extra rows instead of overflowing narrow tables.
 */
function BulkActionOverlay({ actions, selection, onClose, className }: BulkActionOverlayProps) {
  return (
    <div
      role="toolbar"
      aria-label="Bulk actions"
      data-testid="bulk-action-overlay"
      className={cn(
        // w-max: an absolute box at left-1/2 would otherwise only get half the parent's width
        'absolute left-1/2 top-[50%] z-[40] flex w-max max-w-[calc(100%-6rem)] -translate-x-1/2 items-center rounded-lg dark-surface bg-secondary-dark px-3 py-2 shadow-lg',
        className,
      )}
    >
      <div className="flex flex-wrap items-center justify-center gap-y-1">
        {actions.map((action, index) => (
          <React.Fragment key={action.label}>
            {index > 0 && <div className="h-4 w-px mx-1 shrink-0 bg-white/30" aria-hidden />}
            {typeof action.icon === 'string' ? (
              <IconButton
                variant="hollow"
                size="standard"
                icon={action.icon as IconName}
                iconSize={16}
                onClick={() => action.onClick(selection)}
                data-testid={action.testId}
                className="text-white hover:bg-white/10 [&_svg]:text-white h-6"
              >
                {action.label}
              </IconButton>
            ) : (
              <Button
                variant="hollow"
                size="standard"
                onClick={() => action.onClick(selection)}
                data-testid={action.testId}
                className="text-white hover:bg-white/10 [&_svg]:text-white h-6"
              >
                <span className="flex shrink-0 [&_svg]:size-4 [&_svg]:text-white">{action.icon}</span>
                {action.label}
              </Button>
            )}
          </React.Fragment>
        ))}
      </div>
      <IconButton
        variant="hollow"
        size="icon"
        icon="cross2"
        iconSize={16}
        type="button"
        onClick={onClose}
        aria-label="Close bulk actions"
        data-testid="bulk-actions-close"
        className="text-white ml-1 hover:bg-white/10 [&_svg]:text-white h-6"
      />
    </div>
  );
}

export { BulkActionOverlay };
